/**
 * Utility to map technical/database error messages to user-friendly ones.
 */

const ERROR_MAP: Record<string, string> = {
  // Database Constraints
  'uk_clinic_ward_name': 'A ward with this name already exists in your clinic.',
  'uk_ward_bed': 'A bed with this number already exists in this ward.',
  'uk_patient_mobile': 'A patient with this mobile number is already registered.',
  'uk_user_email': 'This email address is already in use.',
  
  // Specific Technical Errors
  "column 'admission_number' cannot be null": 'Failed to generate admission number. Please try again or contact support.',
  'Bed is not available': 'This bed is currently occupied or under maintenance.',
  'Admission not found': 'Could not find the admission record.',
  'Patient not found': 'Could not find the patient record.',
  
  // Generic Categories
  'Access is denied': "You don't have permission to perform this action.",
  'Unauthorized': 'Your session has expired. Please log in again.',
  'Internal Server Error': 'Something went wrong on our end. Please try again later.',
  'Bad Request': 'The request was invalid. Please check your input.',
  'Conflict': 'This operation could not be completed due to a conflict with existing data.',
  
  // Hibernate/Spring/SQL specific markers
  'Duplicate entry': 'A record with similar details already exists.',
  'foreign key constraint': 'This record cannot be deleted because it is linked to other data.',
  'Data truncation': 'One of the values entered is too long or in the wrong format.',
  'JSON parse error': 'The server received data in an unexpected format. Please check your inputs.',
  'could not execute statement': 'A database error occurred while saving your changes.',
  'Constraint violation': 'This record cannot be modified or deleted because it is being used elsewhere.',
  'A technical error occurred': 'Something went wrong on our end. We have been notified and are looking into it.',
  'A database error occurred': 'We could not save your changes due to a database issue. Please try again.',
  'no session': 'Your session has timed out or there was a connection issue. Please refresh the page.',
};

/**
 * Maps a technical error message to a user-friendly one.
 * If no mapping is found, returns the original message unless it looks too technical.
 */
export function getFriendlyErrorMessage(message: string): string {
  if (!message) return 'An unexpected error occurred.';

  // Check for direct matches in our map
  for (const [technical, friendly] of Object.entries(ERROR_MAP)) {
    if (message.includes(technical)) {
      return friendly;
    }
  }

  // Handle common Spring/Hibernate patterns if not specifically caught
  if (message.includes('java.') || message.includes('org.hibernate') || message.includes('SqlException')) {
    return 'A database error occurred. Please contact support.';
  }

  // If it's a generic "An error occurred", keep it simple
  if (message.toLowerCase() === 'an error occurred') {
    return 'Something went wrong. Please try again.';
  }

  return message;
}
